import {BaseDiscrepancyMetrics} from '../models';
import {SortDirection} from '@angular/material/sort';
import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {ManyResponse} from '../../../responses';
import {DiscrepancyFixMalfunctionCode} from '../models/discrepancies/discrepancy-fix-malfunction-code.model';
import {DiscrepancyType} from '../models/discrepancies/discrepancy-type.model';
import {map} from 'rxjs/operators';
import {Cacheable} from 'ts-cacheable';
import {environment} from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TorchDiscrepanciesService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  @Cacheable()
  public sendQueryDiscrepanciesRequest(pageSize: number = 15,
                                       currentPage: number = 0,
                                       sortDirection?: SortDirection,
                                       sortBy?: string,
                                       search?: any) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/many')
      .get;

    const request = {
      sort: null,
      s: null,
      page: (currentPage + 1),
      limit: pageSize
    };


    if (sortDirection && sortBy) {
      request.sort = `${sortBy},${sortDirection.toUpperCase()}`;
    }


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post<ManyResponse<BaseDiscrepancyMetrics>>(endpoint, request);
  }

  @Cacheable()
  public queryDiscrepancies(pageSize: number = 15,
                            currentPage: number = 0,
                            sortDirection?: SortDirection,
                            sortBy?: string,
                            filter?: string | string[],
                            orFilter?: string | string[],
                            search?: any): Observable<ManyResponse<BaseDiscrepancyMetrics>> {

    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addQuery('page', (currentPage + 1))
      .addQuery('limit', pageSize);

    if (sortDirection && sortBy) {
      endpoint.addQuery('sort', `${sortBy},${sortDirection.toUpperCase()}`);
    }

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach(filterValue => {
            finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(filter)}`;
      }
    }

    if (orFilter) {
      if (orFilter instanceof Array) {
        if (orFilter.length > 0) {
          orFilter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&or=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(orFilter)}`;
      }
    }

    if (!!search) {
      finalURL = `${finalURL}&s=${encodeURIComponent(JSON.stringify(search))}`;
    }

    return this.httpClient.get<ManyResponse<BaseDiscrepancyMetrics>>(finalURL);
  }

  @Cacheable()
  public getStatusValues() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/status')
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  @Cacheable()
  public getATAChapterValues() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/ataChapter')
      .get;

    return this.httpClient.get<number[]>(endpoint);
  }

  @Cacheable()
  public getATAValues() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/ata')
      .get;

    return this.httpClient.get<number[]>(endpoint);
  }

  @Cacheable()
  public getFixCodes() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/fixCodes')
      .get;

    return this.httpClient.get<DiscrepancyFixMalfunctionCode[]>(endpoint);
  }

  @Cacheable()
  public getMalfunctionCodes() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/malfunctionCodes')
      .get;

    return this.httpClient.get<DiscrepancyFixMalfunctionCode[]>(endpoint);
  }

  @Cacheable()
  public getDiscrepancyTypes() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/discrepancyTypes')
      .get;

    return this.httpClient.get<DiscrepancyType[]>(endpoint);
  }

  @Cacheable()
  public getFlightPhases() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/values')
      .addSegment('/flightPhases')
      .get;

    return this.httpClient.get<string[]>(endpoint);
  }

  public exportToExcel(discrepancies: BaseDiscrepancyMetrics[]) {
    return new Observable(subscriber => {
      const endpoint = Burly(this.config.rootUrl)
        .addSegment('/discrepancies/')
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType; } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        discreps: discrepancies
      };
      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public fullExportToExcel(search: any, limit: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/discrepancies')
      .addSegment('/exportFull')
      .get;

    const httpOptions: { headers; observe; responseType } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: 'response',
      responseType: 'blob'
    };

    const request = {
      limit: (limit > environment.maxDiscrepancyQueryExport ? environment.maxDiscrepancyQueryExport : limit),
      s: null,
    };


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post(endpoint, request, httpOptions).pipe(
      map(response => (response as unknown as HttpResponse<any>).body)
    );
  }
}
